import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';

// Components
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';

// Shared hook and styles
import {
  createLiveEventFilterStyles,
  useLiveEventFilter,
  getFormValueId,
  LiveSessionWithSubSessions,
} from './use-live-event-filter';

const styles = createLiveEventFilterStyles('live-event-not-registered');

const FIELDS_TO_CLEAR = ['liveEventNotRegisteredEvent'];

const LiveEventNotRegistered = () => {
  const { watch } = useFormContext();

  const [checkedValue, liveEvent, registeredChecked, registeredEvent, registeredSession] = watch([
    'liveEventNotRegisteredChecked',
    'liveEventNotRegisteredEvent',
    'liveEventSessionRegisteredChecked',
    'liveEventSessionRegisteredEvent',
    'liveEventSessionRegisteredSession',
  ]);

  const isEnabled = checkedValue;
  const isRegisteredFilterEnabled = registeredChecked;

  const { liveEventsList, noLiveEvents, buildDropdownOptions, clearFields } = useLiveEventFilter(isEnabled, {
    fieldsToClear: FIELDS_TO_CLEAR,
  });

  // Build live event dropdown options, excluding events that have a session selected in the registered filter
  const liveEventOptions = useMemo(() => {
    let filteredList = liveEventsList;

    // If a session is selected in the registered filter, exclude that live event from the not-registered options
    if (isRegisteredFilterEnabled && registeredSession) {
      const sessionId = getFormValueId(registeredSession);
      const registeredEventId = getFormValueId(registeredEvent);

      // Find the live event that contains the selected session
      const liveEventWithSelectedSession = liveEventsList.find((liveSession: LiveSessionWithSubSessions) => {
        if (registeredEventId && liveSession.id === registeredEventId) {
          return true;
        }
        return liveSession.sessions?.some((s) => s.id === sessionId);
      });

      // Exclude the live event that has a session selected in the registered filter
      if (liveEventWithSelectedSession) {
        filteredList = liveEventsList.filter(
          (liveSession) => liveSession.id !== liveEventWithSelectedSession.id
        );
      }
    }

    return buildDropdownOptions(filteredList);
  }, [liveEventsList, isRegisteredFilterEnabled, registeredEvent, registeredSession, buildDropdownOptions]);

  // Clear live event if it becomes excluded due to a session being selected in the registered filter
  useEffect(() => {
    if (isEnabled && liveEvent && isRegisteredFilterEnabled && registeredSession) {
      const sessionId = getFormValueId(registeredSession);
      const registeredEventId = getFormValueId(registeredEvent);
      const currentEventId = getFormValueId(liveEvent);

      // Check if the currently selected live event is the one that has a session selected in the registered filter
      const isExcluded = liveEventsList.some((liveSession: LiveSessionWithSubSessions) => {
        if (liveSession.id === currentEventId) {
          if (registeredEventId && liveSession.id === registeredEventId) {
            return true;
          }
          return liveSession.sessions?.some((s) => s.id === sessionId);
        }
        return false;
      });

      if (isExcluded) {
        clearFields(['liveEventNotRegisteredEvent']);
      }
    }
  }, [isEnabled, liveEvent, isRegisteredFilterEnabled, registeredEvent, registeredSession, liveEventsList, clearFields]);

  return (
    <div css={styles} className={`px-4 py-2 mb-4 live-event-not-registered ${isEnabled ? 'on' : ''}`}>
      <div className='inline-block mr-2 checkbox-wrapper'>
        <NvCheckbox
          withForm
          name='liveEventNotRegisteredChecked'
          label={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.HAS_NOT_REGISTERED()}
          labelClassName='text-large-regular pl-6 mr-2'
        />
      </div>
      {noLiveEvents && (
        <div className='info text-small text-gray-2 font-weight-bold'>
          {t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.NO_MULTI_SESSION()}
        </div>
      )}
      <div className='dropdowns ml-6'>
        <NvFormDropdown
          name='liveEventNotRegisteredEvent'
          className='live-event'
          items={liveEventOptions}
          title={t.COURSE_COMMUNICATIONS.FILTERS.LIVE_EVENT_SESSION.SELECT_EVENT()}
        />
      </div>
    </div>
  );
};

export default LiveEventNotRegistered;
